import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { payments, subscriptions, type PaymentEvent } from '../api/client';
import { useToast } from './Toast';
import { Button, ErrorState, Input, Spinner } from './ui';

/** How a subscription renders in the picker: `name — group`. */
function optionLabel(name: string, group: { name: string } | null | undefined): string {
  return group?.name ? `${name} — ${group.name}` : name;
}

/**
 * Re-point one payment event at another subscription.
 *
 * The legs, their bills and their rubles belong to the payment, not to the
 * subscription they were filed under, so the move is a single
 * `PATCH /payments/:id { subscriptionId }` — nothing else is sent, and the API
 * refuses an unknown subscription with a 404 rather than guessing. Everything
 * that reads the parent (the drawer header, the reports grouping) follows on
 * its own, because the vendor group is derived from the subscription.
 */
export function MovePaymentForm({
  event,
  onDone,
  onCancel,
}: {
  event: PaymentEvent;
  onDone: (saved: PaymentEvent) => void;
  onCancel: () => void;
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [search, setSearch] = useState('');
  const [targetId, setTargetId] = useState<string | null>(null);

  const list = useQuery({
    queryKey: ['subscriptions', 'move-payment'],
    queryFn: ({ signal }) => subscriptions.list({ page: 1, limit: 200 }, signal),
    staleTime: 60_000,
  });

  // The subscription the payment already sits under is not a destination.
  const options = useMemo(() => {
    const items = list.data?.items.filter((item) => item.id !== event.subscriptionId) ?? [];
    const needle = search.trim().toLowerCase();
    if (!needle) return items;
    return items.filter((item) =>
      `${item.name} ${item.group?.name ?? ''}`.toLowerCase().includes(needle),
    );
  }, [list.data, event.subscriptionId, search]);

  const move = useMutation({
    mutationFn: (subscriptionId: string) => payments.update(event.id, { subscriptionId }),
    onSuccess: (saved) => {
      // The reports bucket rows by the parent subscription's group, so both the
      // matrix and the report regroup from the same invalidated queries.
      queryClient.invalidateQueries({ queryKey: ['payments'] });
      queryClient.invalidateQueries({ queryKey: ['reports'] });
      queryClient.invalidateQueries({ queryKey: ['subscriptions'] });
      const to = saved.subscription?.name ?? saved.subscriptionId;
      toast(`Moved to ${to}`, 'success');
      onDone(saved);
    },
    onError: (error: Error) => toast(error.message, 'error'),
  });

  if (list.isPending) return <Spinner label="Loading subscriptions" />;
  if (list.isError) return <ErrorState message={list.error.message} onRetry={() => list.refetch()} />;

  const target = options.find((item) => item.id === targetId);

  return (
    <form
      onSubmit={(formEvent) => {
        formEvent.preventDefault();
        if (targetId) move.mutate(targetId);
      }}
    >
      <p className="state__hint" style={{ marginTop: 0 }}>
        Currently under <strong>{event.subscription?.name ?? event.subscriptionId}</strong>. The legs, bills and
        rubles stay exactly as they are — only the subscription changes.
      </p>

      <div className="field">
        <label className="field__label" htmlFor="move-search">
          Find a subscription
        </label>
        <Input
          id="move-search"
          type="search"
          placeholder="Search by name or group…"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
      </div>

      <div className="move-list" role="listbox" aria-label="Subscriptions">
        {options.length === 0 ? (
          <p className="state__hint">No other subscription matches.</p>
        ) : (
          options.map((item) => (
            <button
              type="button"
              key={item.id}
              role="option"
              aria-selected={item.id === targetId}
              className={`move-list__option${item.id === targetId ? ' move-list__option--picked' : ''}`}
              onClick={() => setTargetId(item.id)}
            >
              <span>{optionLabel(item.name, item.group)}</span>
            </button>
          ))
        )}
      </div>

      <div className="modal__footer" style={{ padding: 'var(--space-4) 0 0' }}>
        <Button type="button" variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          type="submit"
          variant="primary"
          disabled={!targetId}
          loading={move.isPending}
          title={target ? `Move to ${optionLabel(target.name, target.group)}` : 'Pick a subscription'}
        >
          Move
        </Button>
      </div>
    </form>
  );
}
